/**
 * useCompetitors — конкурентная разведка (SpyWords) для раздела «Конкуренты».
 *  - useCompetitors()        — последний снапшот + тренд расхода (GET)
 *  - useRefreshCompetitors() — живой запрос к SpyWords + сохранить (POST)
 *  - useKeywordLookup()      — спрос/CPC/кто бидует по фразе (GET on-demand)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';

export interface CompetitorOverview {
  keysAd: number;
  uniqAds: number;
  avgPos: number;
  adSpend: number;
  adTraf: number;
  orgTraf: number;
}

export interface CompetitorAd {
  keyword: string;
  adCopy: string;
  pos: string;
  volume: number;
  cpc: number;
}

export interface BrandMetrics {
  keyword: string;
  volume: number;
  volumeTot: number;
  advTot: number;
  avgCpc: number;
}

export interface CompetitorEntry {
  domain: string;
  name: string;
  category: string;
  conquest: boolean;
  /** true — нет своего рекламного домена (экосистема), метрики по бренд-запросу. */
  shared: boolean;
  overview: CompetitorOverview | null;
  ads: CompetitorAd[];
  brand: BrandMetrics | null;
}

export interface CompetitorsTrend {
  labels: string[];
  series: { domain: string; name: string; data: (number | null)[] }[];
}

export interface CompetitorsResponse {
  configured: boolean;
  generatedAt?: string | null;
  competitors?: CompetitorEntry[];
  trend?: CompetitorsTrend;
}

const KEY = ['competitors'];

export function useCompetitors() {
  return useQuery<CompetitorsResponse>({
    queryKey: KEY,
    queryFn: () => apiFetch<CompetitorsResponse>('/api/competitors'),
    staleTime: 5 * 60_000,
  });
}

export function useRefreshCompetitors() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<CompetitorsResponse>('/api/competitors/refresh', { method: 'POST' }),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}

export interface KeywordBidder {
  domain: string;
  adCopy: string;
  avgPos: string;
  keysTot: number;
}

export interface KeywordLookupResponse {
  configured: boolean;
  word?: string;
  overview?: { volume: number; volumeTot: number; advTot: number; avgCpc: number } | null;
  bidders?: KeywordBidder[];
}

export function useKeywordLookup() {
  return useMutation({
    mutationFn: (word: string) =>
      apiFetch<KeywordLookupResponse>(`/api/competitors/keyword?word=${encodeURIComponent(word)}`),
  });
}
